// src/modals/share-canvas.js
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 6615-7022 pre-Plan-37-06).
//
// Scope: pure Canvas drawing for share-card image rendering. No React.
// Public exports: SHARE_PLATFORM_DIMS, layoutAchievementCard, layoutRecommendCard,
//                 renderAchievementCardForPlatform, renderRecommendCardForPlatform,
//                 renderAchievementCard (thin Instagram-square shim).
// Module state: none (pure functions of arguments).

import { CURRENT_YEAR } from '../constants.js';

// Per-platform canvas dimensions — single source of truth for both AchievementModal and RecommendModal.
export const SHARE_PLATFORM_DIMS = {
  x:         { width: 1200, height: 600  },  // 2:1
  facebook:  { width: 1200, height: 630  },  // 1.91:1
  instagram: { width: 1080, height: 1080 },  // 1:1
  linkedin:  { width: 1200, height: 627  },  // 1.91:1
  whatsapp:  { width: 800,  height: 418  },  // 1.91:1, file-size economy
};

// Ratio-based achievement card layout — mutates ctx; caller holds the canvas.
// All pixel literals from the original 1080×1080 design are expressed as ratios
// of width/height so each platform size renders correctly.
export function layoutAchievementCard(ctx, { width, height, monthName, tasksDone, plantCount, plantTypes, colours }) {
  // Scale factor relative to the original 1080×1080 design
  const scale = Math.min(width, height) / 1080;

  // Helper: draw a filled rounded rectangle using arcTo (compatible with all browsers)
  function fillRoundedRect(x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
    ctx.fill();
  }

  // Helper: wrap text within maxWidth, returns array of lines
  function wrapText(text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    for (const word of words) {
      const test = line ? line + ' ' + word : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  // Derived layout values (all ratios of original 1080×1080 design)
  const headerH = Math.round(0.185 * height);   // 200/1080 ≈ 0.185
  const padX    = Math.round(0.056 * width);    // 60/1080 — left/right padding
  const padR    = Math.round(0.944 * width);    // 1020/1080 — right text edge
  const cx      = width / 2;

  // Background — warm off-white
  ctx.fillStyle = '#f7f5f0';
  ctx.fillRect(0, 0, width, height);

  // Header strip — dark green
  ctx.fillStyle = '#2d5a2d';
  ctx.fillRect(0, 0, width, headerH);

  // "Tend" wordmark — left aligned
  ctx.fillStyle = '#f0ede6';
  ctx.font = `600 ${Math.round(72 * scale)}px 'Playfair Display', serif`;
  ctx.textAlign = 'left';
  ctx.fillText('Tend', padX, Math.round(0.120 * height));

  // Month + year — right aligned
  ctx.font = `400 ${Math.round(44 * scale)}px 'Lora', serif`;
  ctx.textAlign = 'right';
  ctx.fillText(`${monthName} ${CURRENT_YEAR}`, padR, Math.round(0.120 * height));

  // Trophy rosette circle — centred horizontally, below header
  const trophyY = Math.round(0.296 * height);   // 320/1080
  const trophyR = Math.round(0.056 * height);   // 60/1080
  ctx.fillStyle = '#2d5a2d';
  ctx.beginPath();
  ctx.arc(cx, trophyY, trophyR, 0, Math.PI * 2);
  ctx.fill();
  // Star inside circle
  ctx.fillStyle = '#f0ede6';
  ctx.font = `400 ${Math.round(52 * scale)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('★', cx, trophyY + Math.round(2 * scale));
  ctx.textBaseline = 'alphabetic';

  // Main heading
  ctx.fillStyle = '#2c2c2c';
  ctx.font = `600 ${Math.round(52 * scale)}px 'Playfair Display', serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Garden Tasks Complete!', cx, Math.round(0.398 * height));  // 430/1080

  // Subtitle
  ctx.fillStyle = '#666';
  ctx.font = `400 ${Math.round(32 * scale)}px 'Lora', serif`;
  const subtitleMaxW = Math.round(0.796 * width);  // 860/1080
  const subtitleLines = wrapText(`All ${tasksDone} garden care tasks finished for ${monthName}.`, subtitleMaxW);
  subtitleLines.forEach((line, i) => {
    ctx.fillText(line, cx, Math.round(0.444 * height) + i * Math.round(42 * scale));  // 480/1080
  });

  // Stats row — 3 columns
  const statsY = Math.round(0.519 * height) + (subtitleLines.length - 1) * Math.round(42 * scale);  // 560/1080
  const stats = [
    { label: 'Tasks done',     value: String(tasksDone) },
    { label: 'Plants tended',  value: String(plantCount) },
    { label: 'Varieties',      value: String(plantTypes) },
  ];
  const colWidth = width / 3;
  stats.forEach((stat, i) => {
    const scx = colWidth * i + colWidth / 2;
    ctx.fillStyle = '#2c2c2c';
    ctx.font = `600 ${Math.round(48 * scale)}px 'Lora', serif`;
    ctx.textAlign = 'center';
    ctx.fillText(stat.value, scx, statsY);
    ctx.fillStyle = '#666';
    ctx.font = `400 ${Math.round(28 * scale)}px 'Lora', serif`;
    ctx.fillText(stat.label, scx, statsY + Math.round(40 * scale));
  });

  // Garden colours bar
  const barY = statsY + Math.round(100 * scale);
  const barH = Math.round(44 * scale);
  const barX = padX;
  const barW = width - padX * 2;
  const gap  = Math.round(6 * scale);

  // Section label
  ctx.fillStyle = '#888';
  ctx.font = `600 ${Math.round(24 * scale)}px 'Lora', serif`;
  ctx.textAlign = 'left';
  ctx.fillText('Colours in the garden this month', barX, barY - Math.round(16 * scale));

  if (colours.length >= 1) {
    const shown = colours.slice(0, 8);
    const shownTotal = shown.reduce((sum, c) => sum + c.count, 0);
    const totalGaps = gap * Math.max(0, shown.length - 1);
    const usableW = barW - totalGaps;
    let x = barX;
    shown.forEach((c, i) => {
      const rawW = Math.floor((c.count / shownTotal) * usableW);
      const segW = i === shown.length - 1 ? (barX + barW - x) : Math.max(16, rawW);
      ctx.fillStyle = c.hex;
      fillRoundedRect(x, barY, segW, barH, 8);
      x += segW + gap;
    });
  } else {
    ctx.fillStyle = '#c8c0b0';
    fillRoundedRect(barX, barY, barW, barH, 8);
  }

  // Divider line
  const divY = barY + barH + Math.round(56 * scale);
  ctx.strokeStyle = '#ddd';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(Math.round(0.185 * width), divY);
  ctx.lineTo(Math.round(0.815 * width), divY);
  ctx.stroke();

  // Footer
  ctx.fillStyle = '#2d5a2d';
  ctx.font = `600 ${Math.round(32 * scale)}px 'Playfair Display', serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Tend — personalised garden planner', cx, divY + Math.round(50 * scale));

  ctx.fillStyle = '#888';
  ctx.font = `400 ${Math.round(26 * scale)}px 'Lora', serif`;
  ctx.fillText('Search for Tend garden planner', cx, divY + Math.round(92 * scale));
}

// Render a correctly-sized achievement card canvas for the given platform channel.
// Uses SHARE_PLATFORM_DIMS as the single source of truth for dimensions.
export async function renderAchievementCardForPlatform(channel, props) {
  const dims = SHARE_PLATFORM_DIMS[channel] || SHARE_PLATFORM_DIMS.facebook;
  const canvas = document.createElement('canvas');
  canvas.width = dims.width;
  canvas.height = dims.height;
  const ctx = canvas.getContext('2d');
  await document.fonts.ready;
  layoutAchievementCard(ctx, { ...props, width: dims.width, height: dims.height });
  return canvas;
}

// Thin shim — preserves the original call site (AchievementModal preview uses 1080×1080 Instagram square).
export async function renderAchievementCard(props) {
  return renderAchievementCardForPlatform('instagram', props);
}

// Static brand card layout for RecommendModal — no user-specific data.
// Reuses SHARE_PLATFORM_DIMS via renderRecommendCardForPlatform.
// Content locked per CONTEXT.md: headline 'Your garden, on Tend', tagline, wordmark, leaf shape.
export function layoutRecommendCard(ctx, { width, height }) {
  // Background — warm off-white (matches Tend brand palette)
  ctx.fillStyle = '#f7f5f0';
  ctx.fillRect(0, 0, width, height);

  // Scale factor: fonts scale proportionally to the smaller dimension
  const scale = Math.min(width, height) / 1080;

  // Iconographic leaf shape above headline — canvas primitives (no Lucide/SVG in 2d context)
  ctx.fillStyle = '#5a7d5a';
  ctx.beginPath();
  const leafCx = width / 2;
  const leafCy = height * 0.24;
  const leafR = Math.round(48 * scale);
  ctx.ellipse(leafCx, leafCy, leafR * 0.6, leafR, Math.PI / 4, 0, Math.PI * 2);
  ctx.fill();

  // Headline — 'Your garden, on Tend'
  ctx.fillStyle = '#2d4a2d';
  ctx.font = `700 ${Math.round(72 * scale)}px 'Playfair Display', serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Your garden, on Tend', width / 2, height * 0.42);

  // Tagline — 'Know exactly what to do this month'
  ctx.fillStyle = '#5a6e5a';
  ctx.font = `400 ${Math.round(36 * scale)}px 'Lora', serif`;
  ctx.fillText('Know exactly what to do this month', width / 2, height * 0.54);

  // Tend wordmark — bottom-centred
  ctx.fillStyle = '#2d4a2d';
  ctx.font = `600 ${Math.round(28 * scale)}px 'Playfair Display', serif`;
  ctx.fillText('Tend', width / 2, height * 0.82);
}

// Dispatcher: creates a per-platform canvas for RecommendModal using SHARE_PLATFORM_DIMS.
// Exported for testing (UAT2-GAP-06).
export async function renderRecommendCardForPlatform(channel) {
  const dims = SHARE_PLATFORM_DIMS[channel] || SHARE_PLATFORM_DIMS.facebook;
  const canvas = document.createElement('canvas');
  canvas.width = dims.width;
  canvas.height = dims.height;
  const ctx = canvas.getContext('2d');
  await document.fonts.ready;
  layoutRecommendCard(ctx, { width: dims.width, height: dims.height });
  return canvas;
}
